import type { RequestedParameterIntentInput } from '../../../api/grpc/requestedParameterIntent';
import type { RevitFamily } from '../../../api/revit/types';
import { getEquivalentRevitParameterNames } from '../../revitParameterNames';
import type { AdditionalCategoriesConfiguration } from '../../../types';
import { fetchDefinitionOnlyFamilySnapshot } from '../../revit/definitionOnlyFamilySnapshot';
import { collectAdditionalCategoryFamilySnapshotParameterNames } from './additionalCategoryPlan';

export interface AdditionalCategoryDefinitionSnapshot {
  families: RevitFamily[];
  available: boolean;
  error?: string;
}

function appendIntent(
  target: Map<string, RequestedParameterIntentInput>,
  parameterName: string,
  destination: 'project' | 'family',
  bindingKind: 'type' | 'instance'
): void {
  getEquivalentRevitParameterNames(parameterName).forEach((name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const key = `${trimmed.toLowerCase()}::${destination}::${bindingKind}`;
    target.set(key, { name: trimmed, destination, bindingKind });
  });
}

export function buildAdditionalCategoryDefinitionIntents(
  additionalCategoriesConfig: AdditionalCategoriesConfiguration,
  objectTypeParameterName: string,
  discriminatorDestination: 'project' | 'family',
  discriminatorBindingKind: 'type' | 'instance'
): RequestedParameterIntentInput[] {
  const intents = new Map<string, RequestedParameterIntentInput>();
  const bindingKind = additionalCategoriesConfig.parameterBindingKind ?? 'instance';
  collectAdditionalCategoryFamilySnapshotParameterNames(
    additionalCategoriesConfig,
    objectTypeParameterName
  ).forEach((name) => {
    appendIntent(intents, name, 'family', bindingKind);
  });
  if (objectTypeParameterName.trim()) {
    appendIntent(
      intents,
      objectTypeParameterName,
      discriminatorDestination,
      discriminatorBindingKind
    );
  }
  return Array.from(intents.values()).sort((left, right) =>
    `${left.name}:${left.destination}:${left.bindingKind}`.localeCompare(
      `${right.name}:${right.destination}:${right.bindingKind}`
    )
  );
}

export async function fetchAdditionalCategoryDefinitionSnapshot(input: {
  additionalCategoriesConfig: AdditionalCategoriesConfiguration;
  objectTypeParameterName: string;
  discriminatorDestination: 'project' | 'family';
  discriminatorBindingKind: 'type' | 'instance';
}): Promise<AdditionalCategoryDefinitionSnapshot> {
  const requestedParameters = buildAdditionalCategoryDefinitionIntents(
    input.additionalCategoriesConfig,
    input.objectTypeParameterName,
    input.discriminatorDestination,
    input.discriminatorBindingKind
  );
  const parameterNames = Array.from(
    new Set(requestedParameters.map((intent) => intent.name))
  ).sort((left, right) => left.localeCompare(right));
  const snapshot = await fetchDefinitionOnlyFamilySnapshot({
    parameterNames,
    requestedParameters,
  });
  return {
    families: snapshot.families,
    available: snapshot.available,
    ...(snapshot.error ? { error: snapshot.error } : {}),
  };
}
